import React from "react";
import "./ConfigurableTable.css";
import SlIcon from "@shoelace-style/shoelace/dist/react/icon";
import Button from "../reusable/Button/Button";

export interface ColumnConfig {
  key: string;
  title: string;
  width?: string;
  align?: "left" | "center" | "right";
  render?: (value: any, item: any, index: number) => React.ReactNode;
  className?: string;
}

export interface ActionButton {
  label: string;
  iconName?: string;
  onClick: (item: any, index: number) => void;
  disabled?: (item: any) => boolean;
  loading?: (item: any) => boolean;
  className?: string | ((item: any) => string);
  theme?: "primary" | "red" | "white" | "blue" | "yellow" | "green" | "danger" | "warning" | "default";
  outlined?: boolean;
  size?: "small" | "medium";
  show?: (item: any) => boolean;
}

export interface ConfigurableTableProps {
  data: any[];
  columns: ColumnConfig[];
  actionButtons?: ActionButton[];
  loading?: boolean;
  className?: string;
  headerClassName?: string;
  rowClassName?: string;
  cellClassName?: string;
  emptyMessage?: string;
  showHeader?: boolean;
  striped?: boolean;
  hoverable?: boolean;
}

const ConfigurableTable: React.FC<ConfigurableTableProps> = ({
  data,
  columns,
  actionButtons = [],
  loading = false,
  className = "",
  headerClassName = "",
  rowClassName = "",
  cellClassName = "",
  emptyMessage = "No hay datos disponibles",
  showHeader = true,
  striped = true,
  hoverable = true,
}) => {

  const renderCell = (column: ColumnConfig, item: any, index: number) => {
    const value = item[column.key];
    
    if (column.render) {
      return column.render(value, item, index);
    }

    const displayValue = value !== null && value !== undefined ? value : "";
    const alignClass = column.align === "left" ? "text-left" : 
                      column.align === "right" ? "text-right" : "text-center";

    return (
      <div className={`${alignClass} ${column.className || ""} w-full`}>
        {displayValue}
      </div>
    );
  };

  const renderActionButtons = (item: any, index: number) => {
    if (actionButtons.length === 0) return null;

    const visibleButtons = actionButtons.filter(button => 
      !button.show || button.show(item)
    );

    if (visibleButtons.length === 0) return null;

    return (
      <div className="flex gap-1">
        {visibleButtons.map((button, btnIndex) => {
          const isDisabled = button.disabled ? button.disabled(item) : false;
          const isLoading = button.loading ? button.loading(item) : false;
          const buttonClassName =
            typeof button.className === "function"
              ? button.className(item)
              : button.className || "";

          return (
            <Button
              key={btnIndex}
              label={button.label}
              iconName={button.iconName}
              theme={button.theme || "primary"}
              outlined={button.outlined || false}
              size={button.size || "small"}
              disabled={isDisabled}
              loading={isLoading}
              className={buttonClassName}
              onClick={() => button.onClick(item, index)}
            />
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="configurable-table-container">
        <div className="flex justify-center items-center py-8">
          <SlIcon name="arrow-repeat" className="animate-spin text-2xl"></SlIcon>
          <span className="ml-2">Cargando...</span>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="configurable-table-container">
        <div className="flex justify-center items-center py-8 text-gray-500">
          <SlIcon name="info-circle" className="mr-2"></SlIcon>
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div className={`configurable-table-container ${className}`}>
      <div className="configurable-table">
        {showHeader && (
          <div className={`configurable-table-header ${headerClassName}`}>
            {columns.map((column, index) => {
              const alignClass = column.align === "left" ? "text-left" : 
                               column.align === "right" ? "text-right" : "text-center";
              return (
                <div
                  key={column.key}
                  className={`configurable-table-header-cell ${alignClass}`}
                  style={{ width: column.width }}
                >
                  <div className="w-full">
                    {column.title}
                  </div>
                </div>
              );
            })}
            {actionButtons.length > 0 && (
              <div className="configurable-table-header-cell text-center">
                <div className="w-full">Acciones</div>
              </div>
            )}
          </div>
        )}
        
        <div className="configurable-table-body">
          {data.map((item, index) => (
            <div
              key={item.id || index}
              className={`
                configurable-table-row
                ${striped && index % 2 === 0 ? "striped" : ""}
                ${hoverable ? "hoverable" : ""}
                ${rowClassName}
              `}
            >
              {columns.map((column) => {
                const alignClass = column.align === "left" ? "text-left" : 
                                 column.align === "right" ? "text-right" : "text-center";
                return (
                  <div
                    key={column.key}
                    className={`configurable-table-cell ${alignClass} ${cellClassName}`}
                    style={{ width: column.width }}
                  >
                    {renderCell(column, item, index)}
                  </div>
                );
              })}
              {actionButtons.length > 0 && (
                <div className="configurable-table-cell text-center">
                  {renderActionButtons(item, index)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ConfigurableTable;
